import React, { useEffect, useState } from 'react';
import './Header.css';
import ThemeToggle from '../components/ThemeToggle';
import NotificationsPanel from '../panels/NotificationsPanel';
import supabase from '../supabaseClient';
import { useMessagesUnread } from '../contexts/MessagesUnreadContext.jsx';
import { useMediaSettings } from '../contexts/MediaSettingsContext.jsx';
import { useHeaderActions } from '../contexts/HeaderActionsContext.jsx';
import {
  fetchNotifications,
  markNotificationRead,
  markAllNotificationsRead,
  deleteNotification,
  deleteAllNotifications
} from './Academy/Academylms/learning/notificationApi';

const Header = ({ currentUser, onToggleSettings, onToggleNotifications, onToggleMessenger, onLogout }) => {
  const [displayName, setDisplayName] = useState('User');
  const [avatarUrl, setAvatarUrl] = useState(null);
  const [notifications, setNotifications] = useState([]);
  const [notificationsOpen, setNotificationsOpen] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  const [showUserDropdown, setShowUserDropdown] = useState(false);

  const { leftSlot } = useHeaderActions();

  const { totalUnread } = useMessagesUnread() || {};
  const unreadMessageCount = totalUnread || 0;
  
  const { openMediaSettings } = useMediaSettings() || {};

  // Fetch notifications
  useEffect(() => {
    if (!currentUser?.id) return;
    fetchNotifications(currentUser.id).then(({ data }) => {
      setNotifications(data || []);
      setUnreadCount((data || []).filter(n => !n.read).length);
    });
  }, [currentUser]);

  // Mark all as read
  const handleMarkAllRead = async () => {
    if (!currentUser?.id) return;
    await markAllNotificationsRead(currentUser.id);
    setNotifications(notifications.map(n => ({ ...n, read: true })));
    setUnreadCount(0);
  };

  // Toggle read/unread
  const handleToggleRead = async (notification) => {
    await markNotificationRead(notification.id);
    setNotifications(
      notifications.map(n =>
        n.id === notification.id ? { ...n, read: !n.read } : n
      )
    );
    setUnreadCount(
      notifications.filter(n => n.id !== notification.id && !n.read).length + (notification.read ? 1 : 0)
    );
  };

  // Delete notification
  const handleDelete = async (notification) => {
    await deleteNotification(notification.id);
    setNotifications(notifications.filter(n => n.id !== notification.id));
    setUnreadCount(
      notifications.filter(n => n.id !== notification.id && !n.read).length
    );
  };

  // Delete all notifications
  const handleDeleteAll = async () => {
    if (!currentUser?.id) return;
    await deleteAllNotifications(currentUser.id);
    setNotifications([]);
    setUnreadCount(0);
  };

  // Open HelpCenter to ticket if notification is support_ticket
  const handleNotificationClick = (notification) => {
    if (notification.type === 'support_ticket' && notification.ticket_id) {
      window.location.href = `/academy/academylms/help-center?ticket=${notification.ticket_id}`;
    }
  };

  // Fetch user profile data including avatar
  useEffect(() => {
    if (!currentUser?.id) return;
    
    const fetchProfile = async () => {
      const { data: profile } = await supabase
        .from('profiles')
        .select('name, avatar_url')
        .eq('id', currentUser.id)
        .single();
      
      if (profile) {
        if (profile.name) setDisplayName(profile.name);
        if (profile.avatar_url) setAvatarUrl(profile.avatar_url);
      }
    };
    
    fetchProfile();
    
    // Fallback display name logic
    if (currentUser) {
      if (currentUser.user_metadata && currentUser.user_metadata.name) {
        setDisplayName(currentUser.user_metadata.name);
      } else if (currentUser.user_metadata && currentUser.user_metadata.display_name) {
        setDisplayName(currentUser.user_metadata.display_name);
      } else if (currentUser.display_name) {
        setDisplayName(currentUser.display_name);
      } else if (currentUser.name) {
        setDisplayName(currentUser.name);
      } else if (currentUser.email) {
        setDisplayName(currentUser.email.split('@')[0]);
      } else {
        setDisplayName('User');
      }
    }
  }, [currentUser]);

  return (
    <header className="auth-main-header">
      <div className="auth-header-inner">
        <div className="auth-header-left">
          <a href="/app/home" aria-label="Home">
            <img className="brand-logo-img" src="/logo.png" alt="VirtuLingu" title="VirtuLingu" style={{ height: 68 }} />
          </a>
          {leftSlot && (
            <div className="auth-header-left-slot">
              {leftSlot}
            </div>
          )}
        </div>
        <div className="auth-header-right">
          <div className="auth-header-actions">
            {/* Messenger Icon */}
            <button
              className="auth-messenger-button"
              onClick={onToggleMessenger}
              aria-label="Messenger"
              title="Open Messenger"
            >
              <svg xmlns="http://www.w3.org/2000/svg" width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5z"></path>
              </svg>
              {unreadMessageCount > 0 && (
                <span className="messengerBadge">{unreadMessageCount}</span>
              )}
            </button>
            
            {/* Notifications Icon */}
            <button
              className="auth-notification-button"
              onClick={() => setNotificationsOpen(v => !v)}
              aria-label="Notifications"
              title="Notifications"
            >
              <svg xmlns="http://www.w3.org/2000/svg" width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9"></path>
                <path d="M13.73 21a2 2 0 0 1-3.46 0"></path>
              </svg>
              {unreadCount > 0 && (
                <span className="notificationBadge">{unreadCount}</span>
              )}
            </button>
            
            <NotificationsPanel
              isOpen={notificationsOpen}
              onClose={() => setNotificationsOpen(false)}
              notifications={notifications}
              onMarkAllRead={handleMarkAllRead}
              onToggleRead={handleToggleRead}
              onDelete={handleDelete}
              onDeleteAll={handleDeleteAll}
              onNotificationClick={handleNotificationClick}
            />
          </div>
          
          {/* User Profile with Dropdown */}
          <div className="auth-user-profile">
            <div 
              className="auth-user-avatar-wrapper"
              onClick={() => setShowUserDropdown(!showUserDropdown)}
            >
              <div className="auth-user-avatar">
                {avatarUrl ? (
                  <img src={avatarUrl} alt={`${displayName}'s avatar`} />
                ) : (
                  <div className="auth-avatar-placeholder">
                    {displayName.charAt(0).toUpperCase()}
                  </div>
                )}
              </div>
              <svg className="auth-dropdown-chevron" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="6 9 12 15 18 9"></polyline>
              </svg>
            </div>
            
            {/* Dropdown Menu */}
            {showUserDropdown && (
              <>
                <div className="auth-dropdown-overlay" onClick={() => setShowUserDropdown(false)} />
                <div className="auth-user-dropdown">
                  <div className="auth-dropdown-header">
                    <span className="auth-user-name">{displayName}</span>
                    <span className="auth-user-email">{currentUser?.email}</span>
                  </div>
                  <div className="auth-dropdown-divider" />
                  <button
                    className="auth-dropdown-item"
                    onClick={() => { onToggleSettings(); setShowUserDropdown(false); }}
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <circle cx="12" cy="12" r="3"></circle>
                      <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1 0 2.83 2 2 0 0 1-2.83 0l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-2 2 2 2 0 0 1-2-2v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83 0 2 2 0 0 1 0-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1-2-2 2 2 0 0 1 2-2h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 0-2.83 2 2 0 0 1 2.83 0l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 2-2 2 2 0 0 1 2 2v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 0 2 2 0 0 1 0 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 2 2 2 2 0 0 1-2 2h-.09a1.65 1.65 0 0 0-1.51 1z"></path>
                    </svg>
                    <span>Settings</span>
                  </button>
                  <button
                    className="auth-dropdown-item"
                    onClick={() => { openMediaSettings?.(); setShowUserDropdown(false); }}
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M12 1a3 3 0 0 0-3 3v8a3 3 0 0 0 6 0V4a3 3 0 0 0-3-3z"></path>
                      <path d="M19 10v2a7 7 0 0 1-14 0v-2"></path>
                      <line x1="12" y1="19" x2="12" y2="23"></line>
                      <line x1="8" y1="23" x2="16" y2="23"></line>
                    </svg>
                    <span>Media Settings</span>
                  </button>
                  <div className="auth-dropdown-item auth-theme-toggle-item">
                    <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <circle cx="12" cy="12" r="5"></circle>
                      <line x1="12" y1="1" x2="12" y2="3"></line>
                      <line x1="12" y1="21" x2="12" y2="23"></line>
                      <line x1="4.22" y1="4.22" x2="5.64" y2="5.64"></line>
                      <line x1="18.36" y1="18.36" x2="19.78" y2="19.78"></line>
                      <line x1="1" y1="12" x2="3" y2="12"></line>
                      <line x1="21" y1="12" x2="23" y2="12"></line>
                      <line x1="4.22" y1="19.78" x2="5.64" y2="18.36"></line>
                      <line x1="18.36" y1="5.64" x2="19.78" y2="4.22"></line>
                    </svg>
                    <span>Theme</span>
                    <ThemeToggle />
                  </div>
                  <div className="auth-dropdown-divider" />
                  <button
                    className="auth-dropdown-item auth-logout-item"
                    onClick={() => { onLogout(); setShowUserDropdown(false); }}
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"></path>
                      <polyline points="16 17 21 12 16 7"></polyline>
                      <line x1="21" y1="12" x2="9" y2="12"></line>
                    </svg>
                    <span>Logout</span>
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </header>
  );
};

export default Header;
